import React, { useEffect, useMemo, useState } from "react";
import { Search, SlidersHorizontal, X } from "lucide-react";
import api from "../lib/api";
import CarCard from "../components/CarCard";

const SORT_OPTIONS = [
  { value: "newest", label: "Newest First" },
  { value: "price_asc", label: "Price: Low to High" },
  { value: "price_desc", label: "Price: High to Low" },
  { value: "year_desc", label: "Year: Newest" },
  { value: "km_asc", label: "Kilometers: Lowest" },
];

const PAGE_SIZE = 15;

export default function InventoryPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({});
  const [sort, setSort] = useState("newest");
  const [search, setSearch] = useState("");
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [options, setOptions] = useState({ brands: [], fuel_types: [], transmissions: [], years: [] });

  useEffect(() => {
    document.title = "Inventory — Faisal Car Bazar";
    api.get("/cars/filters/options").then((r) => setOptions(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { page, page_size: PAGE_SIZE, sort, search: search || undefined, ...filters };
    Object.keys(params).forEach((k) => (params[k] === "" || params[k] == null) && delete params[k]);
    api
      .get("/cars", { params })
      .then((r) => {
        setItems(r.data.items);
        setTotal(r.data.total);
        setTotalPages(r.data.total_pages || 1);
      })
      .finally(() => setLoading(false));
  }, [page, sort, filters, search]);

  const updateFilter = (k, v) => {
    setPage(1);
    setFilters((f) => ({ ...f, [k]: v || undefined }));
  };

  const clear = () => {
    setFilters({});
    setSearch("");
    setPage(1);
  };

  const activeFilterCount = useMemo(
    () => Object.values(filters).filter((v) => v !== undefined && v !== "").length,
    [filters],
  );

  return (
    <div data-testid="inventory-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <div className="mb-10">
        <div className="eyebrow mb-4">Inventory · {total} cars</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#F5F5DC] font-light">
          The complete <span className="text-[#DC143C] italic">collection.</span>
        </h1>
        <div className="luxe-divider" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* FILTERS */}
        <aside
          className={`lg:col-span-3 lg:block ${showMobileFilters ? "block" : "hidden"}`}
          data-testid="inventory-filters"
        >
          <div className="premium-card p-6 sticky top-28">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display text-xl text-[#F5F5DC]">Filters</h3>
              {activeFilterCount > 0 && (
                <button
                  onClick={clear}
                  data-testid="filter-clear"
                  className="text-xs text-[#DC143C] hover:text-[#ED1C24] uppercase tracking-wider"
                >
                  Clear ({activeFilterCount})
                </button>
              )}
            </div>

            <div className="space-y-5">
              <FilterSelect
                label="Brand"
                testId="filter-brand"
                value={filters.brand || ""}
                onChange={(v) => updateFilter("brand", v)}
                options={options.brands}
              />
              <FilterSelect
                label="Fuel"
                testId="filter-fuel"
                value={filters.fuel_type || ""}
                onChange={(v) => updateFilter("fuel_type", v)}
                options={options.fuel_types}
              />
              <FilterSelect
                label="Transmission"
                testId="filter-transmission"
                value={filters.transmission || ""}
                onChange={(v) => updateFilter("transmission", v)}
                options={options.transmissions}
              />
              <div>
                <label className="block text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-2">Year</label>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    data-testid="filter-year-min"
                    type="number"
                    placeholder="Min"
                    value={filters.year_min || ""}
                    onChange={(e) => updateFilter("year_min", e.target.value ? Number(e.target.value) : undefined)}
                    className="bg-[#1A1A1A] border border-white/10 rounded-lg px-3 py-2 text-sm text-[#F5F5DC] focus:border-[#DC143C] outline-none"
                  />
                  <input
                    data-testid="filter-year-max"
                    type="number"
                    placeholder="Max"
                    value={filters.year_max || ""}
                    onChange={(e) => updateFilter("year_max", e.target.value ? Number(e.target.value) : undefined)}
                    className="bg-[#1A1A1A] border border-white/10 rounded-lg px-3 py-2 text-sm text-[#F5F5DC] focus:border-[#DC143C] outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-2">Price (₹)</label>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    data-testid="filter-price-min"
                    type="number"
                    placeholder="Min"
                    value={filters.price_min || ""}
                    onChange={(e) => updateFilter("price_min", e.target.value ? Number(e.target.value) : undefined)}
                    className="bg-[#1A1A1A] border border-white/10 rounded-lg px-3 py-2 text-sm text-[#F5F5DC] focus:border-[#DC143C] outline-none"
                  />
                  <input
                    data-testid="filter-price-max"
                    type="number"
                    placeholder="Max"
                    value={filters.price_max || ""}
                    onChange={(e) => updateFilter("price_max", e.target.value ? Number(e.target.value) : undefined)}
                    className="bg-[#1A1A1A] border border-white/10 rounded-lg px-3 py-2 text-sm text-[#F5F5DC] focus:border-[#DC143C] outline-none"
                  />
                </div>
              </div>
            </div>
            <button
              onClick={() => setShowMobileFilters(false)}
              className="lg:hidden mt-6 w-full btn-primary justify-center"
              data-testid="filters-apply-mobile"
            >
              Apply Filters
            </button>
          </div>
        </aside>

        {/* RESULTS */}
        <div className="lg:col-span-9">
          {/* search + sort */}
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A0A0A0]" />
              <input
                data-testid="inventory-search"
                placeholder="Search by name, brand or model…"
                value={search}
                onChange={(e) => {
                  setPage(1);
                  setSearch(e.target.value);
                }}
                className="w-full bg-[#1A1A1A] border border-white/10 rounded-full pl-11 pr-4 py-3 text-sm text-[#F5F5DC] focus:border-[#DC143C] outline-none"
              />
            </div>
            <select
              data-testid="inventory-sort"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="bg-[#1A1A1A] border border-white/10 rounded-full px-5 py-3 text-sm text-[#F5F5DC] focus:border-[#DC143C] outline-none"
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <button
              onClick={() => setShowMobileFilters((s) => !s)}
              data-testid="filters-toggle-mobile"
              className="lg:hidden btn-secondary justify-center"
            >
              {showMobileFilters ? <X size={16} /> : <SlidersHorizontal size={16} />}
              Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
            </button>
          </div>

          {/* grid */}
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="premium-card aspect-[4/3] animate-pulse" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="premium-card p-12 text-center" data-testid="inventory-empty">
              <p className="font-display text-2xl text-[#F5F5DC]">No cars match your filters.</p>
              <p className="text-sm text-[#A0A0A0] mt-2">Try clearing some filters to see more options.</p>
              <button onClick={clear} className="btn-primary mt-6">Clear All Filters</button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="inventory-grid">
              {items.map((c) => (
                <CarCard key={c.id} car={c} />
              ))}
            </div>
          )}

          {/* pagination */}
          {totalPages > 1 && (
            <div className="mt-12 flex items-center justify-center gap-2" data-testid="inventory-pagination">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-4 py-2 rounded-full border border-white/10 text-sm text-[#F5F5DC] disabled:opacity-30 hover:bg-white/5"
              >
                Previous
              </button>
              {Array.from({ length: totalPages }).map((_, i) => {
                const n = i + 1;
                if (n === 1 || n === totalPages || Math.abs(n - page) <= 1) {
                  return (
                    <button
                      key={n}
                      onClick={() => setPage(n)}
                      data-testid={`page-${n}`}
                      className={`w-10 h-10 rounded-full text-sm transition-all ${
                        n === page
                          ? "bg-[#DC143C] text-white"
                          : "border border-white/10 text-[#F5F5DC] hover:bg-white/5"
                      }`}
                    >
                      {n}
                    </button>
                  );
                }
                if (Math.abs(n - page) === 2) return <span key={n} className="text-[#A0A0A0]">…</span>;
                return null;
              })}
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-4 py-2 rounded-full border border-white/10 text-sm text-[#F5F5DC] disabled:opacity-30 hover:bg-white/5"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options = [], testId }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-2">{label}</label>
      <select
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-[#1A1A1A] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-[#F5F5DC] focus:border-[#DC143C] outline-none"
      >
        <option value="">All</option>
        {options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    </div>
  );
}
